import React, { useState } from 'react';

// Shared "coach note" callout. Self-guards empty body — null/empty string renders nothing.
//
// Props:
//   children   — note body (string or JSX)
//   variant    — 'tactical' (amber) | 'warning' (red) | 'info' (cyan)
//   title      — optional heading override
//   compact    — slimmer padding + smaller text
//   icon       — override default icon
//   expandable — collapsible mode: starts closed, click to open
//   preview    — short preview shown when collapsed (truncated)
//   tips       — array of tip strings shown inside when expanded
const VARIANTS = {
  tactical: {
    bg: 'bg-pressed',
    border: 'border-border',
    titleColor: 'text-text-body',
    bodyColor: 'text-text-body',
    defaultIcon: '💡',
    defaultTitle: 'הערת מאמן',
  },
  warning: {
    bg: 'bg-pressed',
    border: 'border-border',
    titleColor: 'text-text-body',
    bodyColor: 'text-text-body',
    defaultIcon: '⚠️',
    defaultTitle: 'אזהרה',
  },
  info: {
    bg: 'bg-pressed',
    border: 'border-border',
    titleColor: 'text-text-body',
    bodyColor: 'text-text-body',
    defaultIcon: 'ℹ️',
    defaultTitle: 'הערה',
  },
};

export default function CoachNote({
  children,
  variant = 'tactical',
  title,
  compact = false,
  icon,
  className = '',
  expandable = false,
  preview,
  tips = [],
}) {
  const [open, setOpen] = useState(!expandable);

  const isEmpty = children == null
    || (typeof children === 'string' && children.trim() === '');
  if (isEmpty) return null;

  const style = VARIANTS[variant] || VARIANTS.tactical;
  const displayIcon = icon ?? style.defaultIcon;
  const displayTitle = title ?? style.defaultTitle;

  if (!expandable) {
    return (
      <div
        className={`rounded-xl border ${style.bg} ${style.border} ${compact ? 'p-2' : 'p-3'} ${className}`}
      >
        <div className={`${style.titleColor} ${compact ? 'text-[10px]' : 'text-[11px]'} font-bold mb-1 flex items-center gap-1`}>
          <span>{displayIcon}</span>
          <span>{displayTitle}</span>
        </div>
        <div className={`${style.bodyColor} ${compact ? 'text-xs' : 'text-sm'} leading-relaxed`}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setOpen(o => !o)}
      className={`rounded-xl border cursor-pointer transition-all ${style.bg} ${style.border} ${className}`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <span className={`${style.titleColor} text-xs font-medium flex-shrink-0`}>
          {displayIcon} {displayTitle}
        </span>
        {!open && preview && (
          <span className="text-[10px] text-text-body flex-1 truncate">{preview}</span>
        )}
        <span className={`${style.titleColor} text-[10px] opacity-50 mr-auto`}>
          {open ? '▲' : '▼'}
        </span>
      </div>
      {open && (
        <div className="px-3 pb-3">
          <div className={`${style.bodyColor} text-xs leading-relaxed`}>{children}</div>
          {tips.map((tip, i) => (
            <div key={i} className="flex gap-2 mt-1.5">
              <span className={`${style.titleColor} text-[10px] flex-shrink-0 mt-0.5`}>›</span>
              <span className={`${style.bodyColor} text-[10px] leading-relaxed`}>{tip}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
